/**
 * Search UI duplicated from `StockTable` (Parts Inventory). Tailor the request
 * body for `/part/listings` as needed.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { DateRangeType } from "react-tailwindcss-datepicker";

import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";

import { IPartCategory, PartStatus } from "../../../models/Part";
import { ListingStatusEnum } from "../../../models/EbayOffer";
import { IWarehouseLocation } from "../../../models/Location";
import classNames from "../../../utils/classNames";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput";
import SelectInput from "../../shared/Inputs/SelectInput";
import { MakeCombobox } from "../../parts/MakeCombobox";
import { ModelCombobox } from "../../parts/ModelCombobox";
import { TrimCombobox } from "../../parts/TrimCombobox";
import { YearCombobox } from "../../parts/YearCombobox";
import useCategoriesPartial from "../../../data/useCategoriesPartial";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useWarehouseLocations from "../../../data/useWarehouseLocations";
import PopOutBox from "../../shared/PopOutBox";
import CollapsibleSection from "../../shared/CollapsibleSection";
import DateRangeFilter from "../../shared/DateRangeFilter";
import WarehouseLocationSearch from "../../warehouseLocations/WarehouseLocationSearch";

type LocationSearchMode = "equals" | "beginsWith";

type TriState = "any" | "with" | "without";

// Static-options combobox styled to match the Make / Model / Trim / Year
// comboboxes. Allows free text entry too so unusual vehicle attributes
// (e.g. obscure engine codes) can still be searched.
function FilterStaticCombobox({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string) => void;
  options: string[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const [searchTerm, setSearchTerm] = useState(value ?? "");

  useEffect(() => {
    setSearchTerm(value ?? "");
  }, [value]);

  const filtered = useMemo(() => {
    if (!searchTerm) return options;
    const needle = searchTerm.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(needle));
  }, [options, searchTerm]);

  return (
    <div>
      <label>
        <span className="text-sm font-medium text-black">{label}</span>
      </label>
      <Combobox
        as="div"
        value={value ?? ""}
        onChange={(next: string) => {
          onChange(next ?? "");
          setSearchTerm(next ?? "");
        }}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400 rounded-md disabled:bg-gray-100"
            onChange={(event) => {
              setSearchTerm(event.target.value);
              onChange(event.target.value);
            }}
            displayValue={(v: string) => v || searchTerm}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>

          <ComboboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm">
            {filtered.length > 0 ? (
              filtered.slice(0, 100).map((opt) => (
                <ComboboxOption
                  key={opt}
                  value={opt}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-primary-600 text-white" : "text-gray-900"}`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                        {opt}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-primary-600"}`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            ) : (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Press enter to use "{searchTerm}".
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}

// Map the user-facing "Any | With | Without" pills onto the boolean
// `partsWith*` / `partsWithout*` flags the stock search body uses.
// Backend support for these is tracked in docs/pending-endpoints.md.
function buildTriStateFlags(state: {
  hasWorkOrder: TriState | undefined;
  hasImages: TriState | undefined;
  hasMPN: TriState | undefined;
  hasLocation: TriState | undefined;
}): Record<string, true | undefined> {
  const map = (
    v: TriState | undefined,
    withKey: string,
    withoutKey: string
  ): Record<string, true | undefined> => {
    if (v === "with") return { [withKey]: true };
    if (v === "without") return { [withoutKey]: true };
    return {};
  };

  return {
    ...map(state.hasWorkOrder, "partsWithWorkOrder", "partsWithoutWorkOrder"),
    ...map(state.hasImages, "partsWithImages", "partsWithoutImages"),
    ...map(state.hasMPN, "partsWithMPN", "partsWithoutMPN"),
    ...map(state.hasLocation, "partsWithLocation", "partsWithoutLocations"),
  };
}

// Generic segmented-button pill used in the filters strip.
function FilterSegmented<T extends string | number>({
  label,
  value,
  onChange,
  segments,
  disabled = false,
}: {
  label: string;
  value: T | undefined;
  onChange: (next: T) => void;
  segments: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-black">{label}</span>
      <div
        role="group"
        aria-label={label}
        className={classNames(
          "mt-1 inline-flex rounded-md border border-gray-300 bg-white p-0.5",
          disabled ? "opacity-50" : ""
        )}
      >
        {segments.map((seg) => {
          const active = value === seg.value;
          return (
            <button
              key={String(seg.value)}
              type="button"
              disabled={disabled}
              onClick={() => onChange(seg.value)}
              className={classNames(
                "px-2.5 py-1 text-xs font-medium rounded-[5px] transition-colors whitespace-nowrap",
                active
                  ? "bg-primary-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100",
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              )}
              aria-pressed={active}
            >
              {seg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Three-segment Any / With / Without pill used in the filters strip.
function FilterTriToggle({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: TriState | undefined;
  onChange: (next: TriState) => void;
  disabled?: boolean;
}) {
  return (
    <FilterSegmented<TriState>
      label={label}
      value={value ?? "any"}
      onChange={onChange}
      disabled={disabled}
      segments={[
        { value: "any", label: "Any" },
        { value: "with", label: "With" },
        { value: "without", label: "Without" },
      ]}
    />
  );
}

const BODY_STYLE_OPTIONS = [
  "Hatchback",
  "Saloon",
  "Estate",
  "SUV",
  "Coupe",
  "Convertible",
  "MPV",
  "Van",
  "Pickup",
  "Crossover",
];
const DOORS_OPTIONS = ["2", "3", "4", "5"];
const FUEL_OPTIONS = ["Petrol", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid", "LPG"];
const TRANSMISSION_OPTIONS = ["Manual", "Automatic", "Semi-Automatic", "CVT", "DSG"];
const ENGINE_SIZE_OPTIONS = [
  "1.0", "1.2", "1.4", "1.5", "1.6", "1.8",
  "2.0", "2.2", "2.5", "3.0", "3.5", "4.0", "5.0",
];

interface IFormFields {
  make: string;
  model: string;
  trim: string;
  year: string;
  bodyStyle: string;
  numberOfDoors: string;
  fuelType: string;
  transmissionType: string;
  engineSize: string;
  engineCode: string;
  yearFrom: string;
  yearTo: string;
  partNumber: string;
  partCategory: IPartCategory | undefined;
  vrm: string;
  tagNumber: string;
  smartSearch: string;
  dvlaYearOfManufacture: string;
  visibilityDate: string;
  terminateDate: string;
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
  hasWorkOrder: TriState;
  hasImages: TriState;
  hasMPN: TriState;
  hasLocation: TriState;
  partStatus: number;
};

const defaultFormValues: IFormFields = {
  make: "",
  model: "",
  trim: "",
  year: "",
  bodyStyle: "",
  numberOfDoors: "",
  fuelType: "",
  transmissionType: "",
  engineSize: "",
  engineCode: "",
  yearFrom: "",
  yearTo: "",
  partNumber: "",
  partCategory: undefined,
  vrm: "",
  smartSearch: "",
  dvlaYearOfManufacture: "",
  visibilityDate: "",
  terminateDate: "",
  tagNumber: "",
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created",
  hasWorkOrder: "any",
  hasImages: "any",
  hasMPN: "any",
  hasLocation: "any",
  partStatus: -1,
};

export interface UnlistedListViewToggleOption {
  label: string;
}

export interface UnlistedPartSearchFormProps {
  /** When false, search controls stay disabled until the list query layer is ready. */
  pagedTableReady: boolean;
  onApplySearch: (searchBody: Record<string, any>, freeTextQuery: string) => void;
  onClear: () => void;
  /**
   * Optional list view filters (checkboxes) shown in the Filters section.
   * State is owned by the parent to stay in sync with PagedTable.
   */
  listViewFilterOptions?: UnlistedListViewToggleOption[];
  listViewToggles?: boolean[];
  onListViewToggle?: (index: number) => void;
}

export default function UnlistedPartSearchForm({
  pagedTableReady,
  onApplySearch,
  onClear,
  listViewFilterOptions,
  listViewToggles,
  onListViewToggle,
}: UnlistedPartSearchFormProps) {
  const { vrmLookup } = useVehiclesMutations();
  const { categories: allCategories } = useCategoriesPartial();
  const { warehouseLocations } = useWarehouseLocations();
  const { register, setValue, getValues, watch, reset, handleSubmit } = useForm<IFormFields>({
    defaultValues: defaultFormValues,
  });
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const isExtended = true;
  const [isSearching, setIsSearching] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  const [searchBoxOpen, setSearchBoxOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [dates, setDates] = useState<DateRangeType>({ startDate: null, endDate: null });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vehicleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ebayListingSegments = (() => {
    const visibleLabels = ["All", "Published", "Not Published"];
    const all = Object.keys(ListingStatusEnum)
      .filter((key) => isNaN(Number(key)))
      .filter((key) => visibleLabels.includes(key))
      .map((key) => ({
        value: ListingStatusEnum[key as keyof typeof ListingStatusEnum] as number,
        label: key,
      }));
    // Preserve the explicit display order (All first).
    return visibleLabels
      .map((label) => all.find((s) => s.label === label))
      .filter((s): s is { value: number; label: string } => Boolean(s));
  })();
  const ebayAllValue = ListingStatusEnum["All"] as number;

  const partStatusOptions = [
    { id: -1, name: "All" },
    ...Object.keys(PartStatus)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        id: PartStatus[key as keyof typeof PartStatus] as number,
        name: key,
      })),
  ];
  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchTrim = watch("trim");
  const watchYearFrom = watch("yearFrom");
  const watchYearTo = watch("yearTo");
  const watchBodyStyle = watch("bodyStyle");
  const watchNumberOfDoors = watch("numberOfDoors");
  const watchFuelType = watch("fuelType");
  const watchEngineSize = watch("engineSize");
  const watchEngineCode = watch("engineCode");
  const watchTransmission = watch("transmissionType");
  const watchHasWorkOrder = watch("hasWorkOrder");
  const watchHasImages = watch("hasImages");
  const watchHasMPN = watch("hasMPN");
  const watchHasLocation = watch("hasLocation");

  const yearFromNumeric = watchYearFrom ? Number(watchYearFrom) : undefined;
  const yearToNumeric = watchYearTo ? Number(watchYearTo) : undefined;

  const watchListingStatus = watch("listingStatus");
  const watchPartStatus = watch("partStatus");
  const watchWarehouseLocationId = watch("warehouseLocationId");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (typeof watchListingStatus === "number" && watchListingStatus !== ebayAllValue) count += 1;
    if (typeof watchPartStatus === "number" && watchPartStatus >= 0) count += 1;
    if (watchWarehouseLocationId || watchWarehouseLocationBeginsWith) count += 1;
    if (dates.startDate || dates.endDate) count += 1;
    if (watchHasWorkOrder && watchHasWorkOrder !== "any") count += 1;
    if (watchHasImages && watchHasImages !== "any") count += 1;
    if (watchHasMPN && watchHasMPN !== "any") count += 1;
    if (watchHasLocation && watchHasLocation !== "any") count += 1;
    return count;
  }, [
    watchListingStatus,
    watchPartStatus,
    watchWarehouseLocationId,
    watchWarehouseLocationBeginsWith,
    dates.startDate,
    dates.endDate,
    watchHasWorkOrder,
    watchHasImages,
    watchHasMPN,
    watchHasLocation,
    ebayAllValue,
  ]);

  const isSearchDisabled = useMemo(
    () => !pagedTableReady || isSearching || fetchLoading,
    [pagedTableReady, isSearching, fetchLoading]
  );

  const handleGetVehicle = () => {
    const vrm = watch("vrm");
    if (!vrm || fetchLoading || isSearching || isSearchDisabled) return;

    if (vehicleTimeoutRef.current) {
      clearTimeout(vehicleTimeoutRef.current);
    }

    setFetchLoading(true);
    vrmLookup.mutate(vrm, {
      onSuccess: (data) => {
        const vehicle = data.data;
        if (vehicle) {
          setValue("make", (vehicle.combinedMake ?? "").trim() as any);
          setValue("model", (vehicle.modelRangeDescription ?? "").trim() as any);
          setValue("year", vehicle.dvlaYearOfManufacture?.toString() ?? "");

          setValue("bodyStyle", vehicle.bodyStyle);
          setValue("numberOfDoors", vehicle.numberOfDoors?.toString() ?? "");
          setValue("fuelType", vehicle.combinedFuelType);
          setValue("transmissionType", vehicle.combinedTransmission);
          setValue("engineCode", vehicle.engineModelCode);

          const yearFrom = vehicle.visibilityDate && DateTime.fromISO(vehicle.visibilityDate).year;
          const yearTo = vehicle.terminateDate && DateTime.fromISO(vehicle.terminateDate).year;
          setValue("yearFrom", yearFrom?.toString() ?? "");
          setValue("yearTo", yearTo?.toString() ?? "");

          const engineSize = vehicle.engineCapacity
            ? (Math.round(vehicle.engineCapacity / 100) * 100 / 1000).toString()
            : "";
          setValue("engineSize", engineSize);
        }
      },
      onError: () => {},
      onSettled: () => {
        vehicleTimeoutRef.current = setTimeout(() => {
          setFetchLoading(false);
        }, 500);
      },
    });
  };

  const handleSmartSearch = () => {
    const data = getValues();

    if (!data.vrm || isSearching || fetchLoading) return;

    setIsSearching(true);
    setIsSmartMatch(true);
    onApplySearch(
      {
        smartSearch: data.vrm,
        categoryId: data.partCategory?.id,
        listingStatus: data.listingStatus,
      },
      ""
    );

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  const handleClearForm = () => {
    reset();
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    onClear();
    setQueryInput("");
  };

  const onSubmit = (data: IFormFields) => {
    if (isSearching || fetchLoading) return;
    if (!data || typeof data !== "object") return;

    setIsSearching(true);

    let dataCopy: Partial<IFormFields> = {};

    if (!isExtended) {
      dataCopy = {
        ...defaultFormValues,
        make: data.make,
        model: data.model,
        trim: data.trim,
        year: data.year,
        yearFrom: data.yearFrom,
        yearTo: data.yearTo,
        tagNumber: data.tagNumber,
        partNumber: data.partNumber,
        partCategory: data.partCategory,
        listingStatus: data.listingStatus,
        locationSearchMode: data.locationSearchMode,
        warehouseLocationId: data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined,
        warehouseLocationBeginsWith: data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined,
      };
    } else {
      dataCopy = { ...data };
      dataCopy.warehouseLocationId = data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined;
      dataCopy.warehouseLocationBeginsWith = data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined;
    }

    dataCopy.vrm = undefined;
    dataCopy.smartSearch = undefined;
    setIsSmartMatch(false);

    const triFlags = buildTriStateFlags({
      hasWorkOrder: dataCopy.hasWorkOrder,
      hasImages: dataCopy.hasImages,
      hasMPN: dataCopy.hasMPN,
      hasLocation: dataCopy.hasLocation,
    });

    const {
      hasWorkOrder: _hwo,
      hasImages: _hi,
      hasMPN: _hm,
      hasLocation: _hl,
      partStatus: rawPartStatus,
      ...cleanData
    } = dataCopy as IFormFields;

    const bodyWithDates = {
      ...cleanData,
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      dateFieldType: dateFieldType,
      categoryId: dataCopy.partCategory?.id,
      ...triFlags,
      partStatus:
        typeof rawPartStatus === "number" && rawPartStatus >= 0
          ? rawPartStatus
          : undefined,
    };

    onApplySearch(bodyWithDates, queryInput);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  const handleMakeSelect = (make: { make?: string } | null) => {
    setValue("make", (make?.make ?? "") as any);
    setValue("model", "" as any);
    setValue("trim", "" as any);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (vehicleTimeoutRef.current) {
        clearTimeout(vehicleTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm space-y-2">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-3">
          <label className="text-sm font-medium text-black">
                          Get Vehicle Details
                        </label>
            <div
              className={classNames(
                "mt-1 flex items-stretch overflow-hidden rounded-md border-2 border-black shadow-sm",
                "focus-within:ring-2 focus-within:ring-primary-500"
              )}
            >
              <span
                aria-hidden="true"
                className="flex w-6 items-center justify-center bg-blue-700 text-[10px] font-bold uppercase tracking-widest text-yellow-300"
              >
                UK
              </span>
              <input
                {...register("vrm")}
                disabled={fetchLoading || isSearching}
                placeholder="ENTER VRM / VIN"
                aria-label="VRM or VIN"
                className={classNames(
                  fetchLoading || isSearching ? "opacity-60" : "",
                  "w-full border-0 bg-yellow-300 px-3 py-1 text-center text-xl font-bold uppercase tracking-[0.25em] text-black placeholder:text-black/40 placeholder:tracking-[0.15em] placeholder:text-sm focus:outline-none focus:ring-0"
                )}
                style={{
                  fontFamily:
                    '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif',
                }}
              />
              <button
                type="button"
                onClick={handleGetVehicle}
                disabled={isSearchDisabled || fetchLoading}
                title={fetchLoading ? "Fetching..." : "Fetch vehicle details"}
                aria-label="Fetch vehicle details"
                className={classNames(
                  "flex w-9 items-center justify-center bg-black text-yellow-300 transition-colors",
                  isSearchDisabled || fetchLoading
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-slate-800 cursor-pointer"
                )}
              >
                {fetchLoading ? (
                  <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
          <div className="lg:col-span-3">
                <ComboBoxInput
                  label={"Part Type"}
                  register={register}
                  registerName={"partCategory"}
                  options={allCategories.data?.data ?? []}
                  setValue={setValue}
                  disabled={isSearchDisabled || allCategories.isLoading}
                  searchField={"name"}
                  classes="w-full"
                  placeholder="Search categories..."
                  labelClass="text-black"
                />
          </div>
          <div className="lg:col-span-1">
            <label className="text-sm font-medium text-wilma-700">
              Part Number / MPN
            </label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500">
                <HashtagIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <input
                {...register("partNumber")}
                type="text"
                placeholder="MPN..."
                disabled={isSearchDisabled}
                className={classNames(
                  isSearchDisabled ? "bg-gray-100" : "bg-wilma-50",
                  "w-full rounded-md border border-wilma-400 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:text-wilma-400 placeholder:font-normal focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500"
                )}
              />
            </div>
          </div>
          <div className="lg:col-span-2">
            <label className="text-sm font-medium text-wilma-700">
              Tag Number
            </label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500">
                <TagIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <input
                {...register("tagNumber")}
                type="text"
                placeholder="Tag..."
                disabled={isSearchDisabled}
                className={classNames(
                  isSearchDisabled ? "bg-gray-100" : "bg-wilma-50",
                  "w-full rounded-md border border-wilma-400 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:text-wilma-400 placeholder:font-normal focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500"
                )}
              />
            </div>
          </div>
          <div className="lg:col-span-3 flex flex-wrap gap-2 lg:justify-end items-end">
            <ButtonInput
              label={isSearching ? "Searching..." : "Search"}
              isSubmit
              classes={classNames("!h-10 !px-3 !py-0", isSearchDisabled ? "" : "!bg-primary-600 hover:!bg-primary-700")}
              onClick={undefined}
              disabled={isSearchDisabled}
              isLoading={isSearching}
            />
            <ButtonInput
              label="Smart Match"
              onClick={handleSmartSearch}
              classes={classNames("!h-10 !px-3 !py-0", isSearchDisabled || fetchLoading ? "" : "!bg-[#00909c] hover:!bg-[#00b3c2]")}
              disabled={isSearchDisabled || fetchLoading}
              isLoading={isSearching}
            />
            <PopOutBox position="left" classes="z-30 w-96">
              <p>Smart Match will check for parts that fit your vehicle based on key details like make, model, year, and other specifications.</p>
              <p>It finds body parts that match your car's exterior and mechanical parts that fit your engine and transmission.</p>
            </PopOutBox>
            <ButtonInput
              label={"Clear"}
              classes={classNames("!h-10 !px-3 !py-0", isSearchDisabled ? "" : `!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100`)}
              onClick={handleClearForm}
              disabled={isSearchDisabled}
            />
          </div>
        </div>

        <div
          className={classNames(
            isSmartMatch ? "[&_input]:!bg-gray-200" : "",
            "grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-[repeat(19,minmax(0,1fr))]"
          )}
        >
          <div className="xl:col-span-1">
            <YearCombobox
              label="Year From"
              currentValue={watchYearFrom}
              maxYear={yearToNumeric}
              onYearSelect={(year) => setValue("yearFrom", (year ?? "") as any)}
              placeholder="From..."
              disabled={isSearchDisabled}
            />
          </div>
          <div className="xl:col-span-1">
            <YearCombobox
              label="Year To"
              currentValue={watchYearTo}
              minYear={yearFromNumeric}
              onYearSelect={(year) => setValue("yearTo", (year ?? "") as any)}
              placeholder="To..."
              disabled={isSearchDisabled}
            />
          </div>
          <div className="xl:col-span-2">
            <MakeCombobox label="Make" onMakeSelect={handleMakeSelect} currentValue={watchMake} />
          </div>
          <div className="xl:col-span-2">
            <ModelCombobox
              label="Model"
              make={watchMake}
              onModelSelect={(model) => {
                setValue("model", (model?.range ?? "") as any);
                setValue("trim", "" as any);
              }}
              currentValue={watchModel}
            />
          </div>
          <div className="xl:col-span-2">
            <TrimCombobox
              label="Trim"
              make={watchMake}
              model={watchModel}
              onTrimSelect={(trim) => setValue("trim", (trim ?? "") as any)}
              currentValue={watchTrim}
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Body Style"
              value={watchBodyStyle}
              onChange={(v) => setValue("bodyStyle", v as any)}
              options={BODY_STYLE_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-1">
            <FilterStaticCombobox
              label="Doors"
              value={watchNumberOfDoors}
              onChange={(v) => setValue("numberOfDoors", v as any)}
              options={DOORS_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Fuel"
              value={watchFuelType}
              onChange={(v) => setValue("fuelType", v as any)}
              options={FUEL_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Engine Size"
              value={watchEngineSize}
              onChange={(v) => setValue("engineSize", v as any)}
              options={ENGINE_SIZE_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Engine Code"
              value={watchEngineCode}
              onChange={(v) => setValue("engineCode", v as any)}
              options={[]}
              disabled={isSearchDisabled}
              placeholder="Type code..."
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Transmission"
              value={watchTransmission}
              onChange={(v) => setValue("transmissionType", v as any)}
              options={TRANSMISSION_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
        </div>

        {isSmartMatch && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2">
            <p className="text-blue-800 text-sm">
              <strong>Smart Match Active:</strong> Searching for parts that match your vehicle specifications.
              Vehicle details are locked and cannot be modified while smart match is active.
            </p>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50/60">
          <button
            type="button"
            onClick={() => setSearchBoxOpen((v) => !v)}
            aria-expanded={searchBoxOpen}
            className="flex w-full items-center justify-between gap-3 rounded-t-lg px-3 py-2 text-left transition-colors hover:bg-slate-100/70"
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <MagnifyingGlassIcon
                className="h-4 w-4 shrink-0 text-slate-500"
                aria-hidden="true"
              />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Search
              </span>
              {!searchBoxOpen && queryInput.trim() && (
                <span
                  className="truncate text-[11px] font-medium text-slate-500"
                  title={queryInput}
                >
                  {queryInput.trim()}
                </span>
              )}
            </span>
            {searchBoxOpen ? (
              <ChevronUpIcon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            )}
          </button>

          <CollapsibleSection isOpen={searchBoxOpen}>
            <div className="px-3 pb-3 pt-0">
              <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                <div className="w-44 shrink-0">
                  <SelectInput
                    label={"Part Status"}
                    register={register}
                    registerName={"partStatus"}
                    options={partStatusOptions}
                    setValue={setValue}
                    watch={watch}
                    cols="w-full"
                    disabled={isSearchDisabled}
                    rounded
                    labelClass="text-black"
                  />
                </div>
                <div className="relative min-w-[12rem] flex-1">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    type="text"
                    value={queryInput}
                    disabled={isSearchDisabled}
                    onChange={(e) => setQueryInput(e.target.value)}
                    className="w-full min-w-0 rounded-md border-1 border-gray-300 pl-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                    placeholder="Search unlisted parts by tag, MPN, make, model, engine code, category, or warehouse"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50/60">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-100/70 transition-colors"
          >
            <span className="flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Filters
              </span>
              {activeFiltersCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-wilma-100 px-2 py-0.5 text-[11px] font-semibold text-wilma-700">
                  {activeFiltersCount} active
                </span>
              )}
              {!filtersOpen && activeFiltersCount === 0 && (
                <span className="text-[11px] font-medium text-slate-400">
                  No filters applied
                </span>
              )}
            </span>
            {filtersOpen ? (
              <ChevronUpIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
            )}
          </button>

          <CollapsibleSection isOpen={filtersOpen}>
            <div className="px-3 pt-1 pb-3">
              <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                <FilterSegmented<number>
                  label="eBay Status"
                  value={watchListingStatus ?? ebayAllValue}
                  onChange={(v) => setValue("listingStatus", v as any)}
                  segments={ebayListingSegments}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="Work Order"
                  value={watchHasWorkOrder}
                  onChange={(v) => setValue("hasWorkOrder", v as any)}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="Images"
                  value={watchHasImages}
                  onChange={(v) => setValue("hasImages", v as any)}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="MPN"
                  value={watchHasMPN}
                  onChange={(v) => setValue("hasMPN", v as any)}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="Location"
                  value={watchHasLocation}
                  onChange={(v) => setValue("hasLocation", v as any)}
                  disabled={isSearchDisabled}
                />

                <WarehouseLocationSearch
                  showSearchOptions={true}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  isSearchDisabled={isSearchDisabled}
                  warehouseLocations={warehouseLocations}
                />

                <DateRangeFilter
                  dates={dates}
                  onDatesChange={handleDatesChange}
                  dateFieldType={dateFieldType}
                  onDateFieldTypeChange={setDateFieldType}
                  disabled={isSearchDisabled}
                />
              </div>

              {listViewFilterOptions && listViewFilterOptions.length > 0 && listViewToggles && onListViewToggle && (
                <div className="mt-3 border-t border-slate-200 pt-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    List view filters
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {listViewFilterOptions.map((opt, i) => (
                      <label
                        key={opt.label}
                        className={classNames(
                          "inline-flex cursor-pointer select-none items-center gap-1.5 text-sm text-slate-600",
                          isSearchDisabled && "cursor-not-allowed opacity-50"
                        )}
                      >
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                          checked={Boolean(listViewToggles[i])}
                          onChange={() => onListViewToggle(i)}
                          disabled={isSearchDisabled}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>

      </form >
    </>
  );
}
